import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff, CheckCircle, Lock, User, Pencil, X, Save } from "lucide-react";
import { changePassword, updateProfile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TaxAssumptions } from "@/components/TaxAssumptions";
import { SubscriptionManager } from "@/components/SubscriptionManager";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileMobile, setProfileMobile] = useState("");
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Initialize profile fields when user loads or edit mode starts
  useEffect(() => {
    if (user && isEditingProfile) {
      setProfileName(user.name || "");
      setProfileMobile(user.mobile || "");
    }
  }, [user, isEditingProfile]);

  const handleEditProfile = () => {
    setProfileName(user?.name || "");
    setProfileMobile(user?.mobile || "");
    setProfileError("");
    setIsEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setProfileError("");
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError("");

    if (profileName.trim().length < 2) {
      setProfileError("Name must be at least 2 characters");
      return;
    }

    if (profileMobile.trim().length < 8) {
      setProfileError("Please enter a valid mobile number");
      return;
    }

    // Check if anything changed
    if (profileName === user?.name && profileMobile === user?.mobile) {
      setIsEditingProfile(false);
      return;
    }

    setIsProfileLoading(true);

    try {
      const updateData = {};
      if (profileName !== user?.name) updateData.name = profileName;
      if (profileMobile !== user?.mobile) updateData.mobile = profileMobile;
      
      await updateProfile(updateData);
      toast.success("Profile updated successfully");
      setIsEditingProfile(false);
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      } else {
        // Fallback: reload page to get updated user data
        window.location.reload();
      }
    } catch (err) {
      setProfileError(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    setIsPasswordLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully");
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-heading text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account settings</p>
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5" />
              Account Information
            </CardTitle>            {!isEditingProfile && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditProfile}
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditingProfile ? (
            <form onSubmit={handleSaveProfile} className="space-y-4 max-w-md">
              {profileError && (
                <Alert variant="destructive">
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="profileName">Name</Label>
                <Input
                  id="profileName"
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Your name"
                  required
                  data-testid="profile-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profileEmail">Email</Label>
                <Input
                  id="profileEmail"
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-slate-50 text-slate-500"
                />
                <p className="text-xs text-slate-500">Email cannot be changed</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profileMobile">Mobile Number</Label>
                <Input
                  id="profileMobile"
                  type="tel"
                  value={profileMobile}
                  onChange={(e) => setProfileMobile(e.target.value)}
                  placeholder="0412 345 678"
                  required
                  data-testid="profile-mobile-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="submit"
                  className="bg-[#547d91] hover:bg-[#456a7a]"
                  disabled={isProfileLoading}
                  data-testid="save-profile-btn"
                >
                  {isProfileLoading ? (
                    "Saving..."
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isProfileLoading}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Name</Label>
                <p className="font-medium">{user?.name || "N/A"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Email</Label>
                <p className="font-medium">{user?.email || "N/A"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Mobile</Label>
                <p className="font-medium">{user?.mobile || "N/A"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Account Type</Label>
                <p className="font-medium capitalize">{user?.auth_type || "Email"}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription */}
      <SubscriptionManager />

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            {passwordError && (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            
            {passwordSuccess && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Password changed successfully!
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords ? "text" : "password"}
                  placeholder="••••••••"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPasswords(!showPasswords)}
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type={showPasswords ? "text" : "password"}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type={showPasswords ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              disabled={isPasswordLoading}
              data-testid="change-password-submit"
            >
              {isPasswordLoading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Tax Assumptions */}
      <TaxAssumptions />
    </div>
  );
}
